import * as React from "react";
import {
  Grid,
  TextField,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { getYodaDateToday } from "../utils/DateHelper";
import { getHeaders } from "../utils/HeaderHelper";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckboxList from "../common/CheckboxList";
import NewTextField from "../form/NewTextField";

const accordionHeight = "20px";

interface Props {
  csrfToken: string;
  setSearchPatients: (patients: any[]) => void;
  fixedProgramId?: number;
  selectedPatients: any[];
  setSelectedPatients: (patients: any[]) => void;
  setOnList: (patients: any[]) => void;
  pendingAdd: any[];
  setParamsPresent: (present: boolean) => void;
  setLoading: (loading: boolean) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  setTotalPages: (pages: number) => void;
  sortObject: { field: string; direction: "asc" | "desc" };
}

const SearchToolsWidget: React.FC<Props> = (props: any) => {
  const [diagnosisCodesExpanded, setDiagnosisCodesExpanded] =
    React.useState(false);
  const [insuranceTypesExpanded, setInsuranceTypesExpanded] =
    React.useState(false);
  const [ltcFacilitiesExpanded, setLtcFacilitiesExpanded] =
    React.useState(false);
  const [cgmExpanded, setCGMExpanded] = React.useState(false);

  const [firstName, setFirstName] = React.useState<string>("");
  const [lastName, setLastName] = React.useState<string>("");
  const [dateOfBirth, setDateOfBirth] = React.useState<string>("");
  const [tagCount, setTagCount] = React.useState<number>(null);

  const [checkedDiagnosisCodes, setCheckedDiagnosisCodes] = React.useState<
    string[]
  >([]);
  const [checkedInsuranceTypes, setCheckedInsuranceTypes] = React.useState<
    string[]
  >([]);
  const [checkedLtcFacilities, setCheckedLtcFacilities] = React.useState<
    string[]
  >([]);
  const [checkedCGM, setCheckedCGM] = React.useState<boolean>(false);

  const [diagnosisCodes, setDiagnosisCodes] = React.useState<[]>([]);
  const [insuranceTypes, setInsuranceTypes] = React.useState<[]>([]);
  const [ltcFacilities, setLtcFacilities] = React.useState<[]>([]);

  const hasMounted = React.useRef(false);
  const timeoutId: any = React.useRef(null);

  const accordions = [
    {
      label: "Diagnosis Codes",
      expanded: diagnosisCodesExpanded,
      setExpanded: setDiagnosisCodesExpanded,
      tag: diagnosisCodes.length,
      items: diagnosisCodes,
      checkedItems: checkedDiagnosisCodes,
      setCheckedItems: setCheckedDiagnosisCodes,
      labelField: "diagnosis_code_value",
    },
    {
      label: "Insurance Types",
      expanded: insuranceTypesExpanded,
      setExpanded: setInsuranceTypesExpanded,
      tag: insuranceTypes.length,
      items: insuranceTypes,
      checkedItems: checkedInsuranceTypes,
      setCheckedItems: setCheckedInsuranceTypes,
      labelField: "insurance_type",
    },
    {
      label: "Living Facilities",
      expanded: ltcFacilitiesExpanded,
      setExpanded: setLtcFacilitiesExpanded,
      tag: ltcFacilities.length,
      items: ltcFacilities,
      checkedItems: checkedLtcFacilities,
      setCheckedItems: setCheckedLtcFacilities,
      labelField: "name",
    },
    {
      label: "CGM",
      expanded: cgmExpanded,
      setExpanded: setCGMExpanded,
      tag: 0,
    },
  ];

  const initialFetch = async () => {
    try {
      props.setCurrentPage(1);
      props.setLoading(true);

      const params = new URLSearchParams({
        page: "1",
      });

      const response = await fetch(
        `/programs/${
          props.fixedProgramId
        }/program_patients?${params.toString()}`,
        {
          method: "GET",
          headers: getHeaders(props.csrfToken),
        }
      );

      const data = await response.json();

      setDiagnosisCodes(data.diagnosis_codes || []);
      setInsuranceTypes(data.insurance_types || []);
      setLtcFacilities(data.ltc_facilities || []);
      setTagCount(data.tag_count || 0);

      const patients = data.patients || [];
      if (patients.length > 0) {
        props.setSearchPatients(patients);
        const onListPatients = patients.filter((p: any) => p.on_list);
        props.setSelectedPatients(onListPatients);
        props.setOnList(onListPatients);
      }

      props.setTotalPages(data.total_pages || 1);
    } catch (error) {
      console.error("Initial fetch failed:", error);
    } finally {
      props.setLoading(false);
    }
  };

  const shouldFetchPatients = () => {
    return (
      (firstName && firstName.length >= 2) ||
      (lastName && lastName.length >= 2) ||
      dateOfBirth ||
      checkedDiagnosisCodes.length > 0 ||
      checkedInsuranceTypes.length > 0 ||
      checkedLtcFacilities.length > 0 ||
      checkedCGM
    );
  };

  const clearAllFilters = () => {
    setFirstName("");
    setLastName("");
    setDateOfBirth("0000-00-00");
    setTimeout(() => setDateOfBirth(""), 0);
    setCheckedDiagnosisCodes([]);
    setCheckedInsuranceTypes([]);
    setCheckedLtcFacilities([]);
    setCheckedCGM(false);
  };

  const getPatients = (resetPages = false) => {
    props.setLoading(true);
    const params = new URLSearchParams();
    params.append("sort_by", props.sortObject.field);
    params.append("sort_direction", props.sortObject.direction);

    if (firstName) params.append("first_name", firstName);
    if (lastName) params.append("last_name", lastName);
    if (dateOfBirth) params.append("date_of_birth", dateOfBirth);

    if (checkedDiagnosisCodes.length > 0) {
      checkedDiagnosisCodes.forEach((codeId) =>
        params.append("diagnosis_codes[]", codeId)
      );
    }

    if (checkedInsuranceTypes.length > 0) {
      checkedInsuranceTypes.forEach((insuranceId) =>
        params.append("insurance_types[]", insuranceId)
      );
    }

    if (checkedLtcFacilities.length > 0) {
      checkedLtcFacilities.forEach((facilityId) =>
        params.append("ltc_facilities[]", facilityId)
      );
    }

    params.append("is_cgm", checkedCGM.toString());

    if (!params.toString()) {
      return;
    }

    if (resetPages) {
      params.append("page", "1");
      props.setCurrentPage(1);
    } else {
      params.append("page", props.currentPage);
    }

    fetch(
      `/programs/${props.fixedProgramId}/program_patients?${params.toString()}`,
      {
        method: "GET",
        headers: getHeaders(props.csrfToken),
      }
    )
      .then((result) => result.json())
      .then((result) => {
        props.setLoading(false);

        if (result.error) {
          console.log("Error fetching patients:", result.error);
        } else {
          props.setSearchPatients(
            result.patients.map((patient) => {
              const isChecked = props.selectedPatients.some(
                (checked) => checked.id === patient.id
              );
              if (patient.list_status == "Not on List" && isChecked) {
                return { ...patient, list_status: "Pending Add" };
              } else if (patient.list_status == "On List" && !isChecked) {
                return { ...patient, list_status: "Pending Removal" };
              } else {
                return patient;
              }
            })
          );

          props.setTotalPages(result.total_pages);
        }
      })
      .catch((error) => {
        props.setLoading(false);

        console.log("Fetch error:", error);
      });
  };

  React.useEffect(() => {
    clearTimeout(timeoutId.current);
    timeoutId.current = setTimeout(() => {
      if (shouldFetchPatients()) {
        getPatients(true);
      } else if (hasMounted.current) {
        getPatients(true);
      }
      props.setParamsPresent(shouldFetchPatients());
      hasMounted.current = true;
    }, 250);
  }, [
    firstName,
    lastName,
    dateOfBirth,
    checkedDiagnosisCodes,
    checkedInsuranceTypes,
    checkedLtcFacilities,
    checkedCGM,
    props.sortObject,
  ]);

  React.useEffect(() => {
    initialFetch();
  }, []);

  React.useEffect(() => {
    if (!hasMounted.current) return;
    getPatients();
  }, [props.currentPage]);

  return (
    <Grid item xs={12}>
      <Grid container>
        <Grid
          container
          justifyContent="space-between"
          className="widget-container"
        >
          <Grid item xs={12} className="widget-header">
            <h3>Search Tools</h3>
          </Grid>
        </Grid>
        <div className="divider"></div>
        <Grid container>
          <Grid item xs={12} className="widget-body">
            <div className="filter-container">
              <div className="filter-header">
                <div className="header-wrapper">
                  <div className="header">Patient Filters</div>
                  {shouldFetchPatients() && (
                    <button onClick={clearAllFilters} className="clear-all">
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 8 8"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          y="7.99902"
                          width="8"
                          height="8"
                          rx="1"
                          transform="rotate(-90 0 7.99902)"
                          fill="#FF890A"
                        />
                        <path
                          d="M2.13699 1.66585L1.66699 2.13585L3.53033 3.99919L1.66699 5.86252L2.13699 6.33252L4.00033 4.46919L5.86366 6.33252L6.33366 5.86252L4.47033 3.99919L6.33366 2.13585L5.86366 1.66585L4.00033 3.52919L2.13699 1.66585Z"
                          fill="white"
                        />
                      </svg>
                      <span>Clear All</span>
                    </button>
                  )}
                </div>
                <div className="tag-number">
                  {tagCount > 0 ? tagCount : 0} existing patient tags
                </div>
              </div>

              <div className="filter-body">
                <Grid item xs={12} className="field-container">
                  <NewTextField
                    id="first_name"
                    label="First Name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="First Name"
                  />
                </Grid>
                <Grid item xs={12} className="field-container">
                  <NewTextField
                    id="last_name"
                    label="Last Name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="First Name"
                  />
                </Grid>
                <Grid item xs={12} className="field-container">
                  <TextField
                    id="date"
                    label="Date of birth"
                    value={dateOfBirth}
                    className="textInput plainLabel small-label"
                    type="date"
                    variant="filled"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ disableUnderline: true }}
                    inputProps={{ max: `${getYodaDateToday()}` }}
                    onChange={(event) => setDateOfBirth(event.target.value)}
                  />
                </Grid>
              </div>
              <div className="tool-list">
                {accordions.map(
                  (
                    {
                      label,
                      expanded,
                      setExpanded,
                      tag,
                      items,
                      checkedItems,
                      setCheckedItems,
                      labelField,
                    },
                    index
                  ) => (
                    <Accordion
                      key={index + 1}
                      expanded={expanded}
                      onChange={() => setExpanded(!expanded)}
                      sx={{
                        boxShadow: "none",
                        border: "none",
                        "&:before": { display: "none" },
                        "&. Mui-expanded": { margin: 0 },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={null}
                        sx={{
                          padding: 0,
                          minHeight: accordionHeight,
                          "&.MuiAccordionSummary-root": {
                            minHeight: accordionHeight,
                          },
                          "&.MuiAccordionSummary-gutters": {
                            padding: 0,
                            minHeight: accordionHeight,
                          },
                          "&.Mui-expanded": { minHeight: accordionHeight },
                          "& .MuiAccordionSummary-content": {
                            margin: 0,
                            alignItems: "center",
                            minHeight: accordionHeight,
                          },
                          "& .MuiAccordionSummary-contentGutters": {
                            marginBottom: "10px",
                            minHeight: accordionHeight,
                          },
                          "& .MuiAccordionSummary-content.Mui-expanded": {
                            margin: 0,
                            minHeight: accordionHeight,
                            alignItems: "center",
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            p: "0 20px 0 12px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              transform: expanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease-in-out",
                            }}
                          >
                            <ArrowDropDownIcon />
                          </Box>
                          <p className="collapse-list-header">{label}</p>
                          {label !== "CGM" ? (
                            <div className="tool-badge">{tag}</div>
                          ) : (
                            <div></div>
                          )}
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          padding: 0,
                          backgroundColor: "#F8F8F8",
                          p: "20px 20px 12px 16px",
                        }}
                      >
                        {label === "CGM" ? (
                          <div className="multiple-checkbox">
                            <div className="checkbox-row-container">
                              <label className="option-text">
                                <input
                                  type="checkbox"
                                  onChange={(e) =>
                                    setCheckedCGM(e.target.checked)
                                  }
                                  checked={checkedCGM}
                                  className="checkbox"
                                />
                                <span>CGM Enabled</span>
                              </label>
                            </div>
                          </div>
                        ) : (
                          <CheckboxList
                            items={items}
                            checkedItems={checkedItems}
                            setCheckedItems={setCheckedItems}
                            labelField={labelField}
                          />
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SearchToolsWidget;
